import {
    AIActionType,
    PlanningContext,
    PlanningState,
    TacticalNode
} from "@/core/battle/ai/types/ai.types";
import { EvaluatorUtils } from "@/core/battle/ai/utils/evaluatorUtils";
import { BattleUnit } from "@/core/types/battle.types";
import { getUnitTotalHp } from "@/core/utils/battle.utils";
import { coordsToId } from "@/core/utils/hex.utils";

export class TerminalEvaluator {
    evaluate(state: PlanningState, ctx: PlanningContext): number {
        const targetHex = coordsToId(state.position);
        const tacticalNode = ctx.tacticalMap.nodes[targetHex];
        const threatNode = ctx.threatMap.get(targetHex) || 0;
        const protectionNode = ctx.protectionMap.get(targetHex) || 0;
        
        if (!tacticalNode) return 0;

        let totalLethality = 0;
        let totalFeasibility = 1.0;

        let isAttackExecuted = false;
    
        for (const action of state.actionsUsed) {
            if (action.type === AIActionType.Attack) {
                isAttackExecuted = true;
                totalLethality += action.targetUnitId === ctx.goal?.targetId ? action.utility : action.utility * (1.0 - ctx.weights.DISCIPLINE); 
            } 
            if (action.type === AIActionType.Move) {
                const segmentSafety = action.utility; 

                totalFeasibility *= segmentSafety;
            }
        }
        const finalFeasibility = Math.max(0.1, totalFeasibility);
        const combatScore = Math.min(1.0, totalLethality);

        // ОЦІНКА ГЕКСА
        const { proximity, lethality, offense } = this.scoreHex(
            tacticalNode, 
            threatNode, 
            protectionNode,
            ctx,
            targetHex
        );
        
        const staminaBonus = state.stamina / 10000000;
        const minSafety = Math.max(ctx.weights.MIN_SAFETY, (1 - (lethality * ctx.weights.RISK_WEIGHT)));
        // const finalSafety = (minSafety + (1 - minSafety) * (1 - ctx.weights.MIN_SAFETY));
        const finalSafety = minSafety;
        const finalProximity = proximity * 0.01;
        const offenseWeight = isAttackExecuted ? 0 : offense;
        const hexScore = finalProximity * finalSafety + staminaBonus;

        // const hexScoreMultipler = ctx.goal?.targetPosition ? 0.7 : 1;
        // const combatScoreMultipler = ctx.goal?.targetPosition ? 0.3 : 1;

        const hexScoreMultipler = 1;
        const combatScoreMultipler = 1;
        // 3. ФІНАЛЬНИЙ МІКС
        
         
        const finalScore = ((hexScore * hexScoreMultipler) + ((combatScore + offenseWeight) * combatScoreMultipler)) * finalFeasibility;
        // if (offense > 0) {
        //     console.info(
        //         `[Terminal] ${ctx.unit.unitId} @ ${targetHex}`,
        //         {
        //             final: finalScore.toFixed(3),
        //             hex: hexScore.toFixed(3),
        //             combat: combatScore.toFixed(3),
        //             offense: offense.toFixed(3),
        //             feasibility: finalFeasibility.toFixed(3),
        //             proximity: proximity.toFixed(3),
        //             lethality: lethality.toFixed(3),
        //             finalSafety: finalSafety.toFixed(3),
        //             stamina: state.stamina,
        //             actions: state.actionsUsed.map(a => `${a.type}(${a.utility.toFixed(2)})`).join(' → ')
        //         }
        //     );
        // } else {
            // console.log(
            //     `[Terminal] ${ctx.unit.unitId} @ ${targetHex} step: ${ctx.round}`,
            //     {
            //         final: finalScore.toFixed(5),
            //         hex: hexScore.toFixed(5),
            //         combat: combatScore.toFixed(5),
            //         offense: offense.toFixed(5),
            //         feasibility: finalFeasibility.toFixed(5),
            //         proximity: proximity.toFixed(5),
            //         lethality: lethality.toFixed(5),
            //         finalSafety: finalSafety.toFixed(5),
            //         stamina: state.stamina,
            //         actions: state.actionsUsed.map(a => `${a.type}(${a.utility.toFixed(2)})`).join(' → ')
            //     }
            // );

        // }
        return finalScore;
    }

    private scoreHex(
        tacticalNode: TacticalNode,
        rawThreat: number,
        protectionValue: number,
        ctx: PlanningContext,
        hexId: string,
    ): { proximity: number, lethality: number, offense: number } {
        const unit = ctx.unit;
        const stats = ctx.tacticalMap.stats;
        const goal = ctx.goal;
        const dist: number = tacticalNode.dist;
        let currentDirectDist: number = tacticalNode.directDist ?? 99;
        const moveCost: number = tacticalNode.moveCost;

        // --- 1. PROXIMITY (Бажання підійти) ---
        const isReposition = goal?.type === 'REPOSITION';
        const optimalDist = unit.skills[0]?.range || 1;
        
        // const penalty = (!isReposition && optimalDist > 1 && directDist < optimalDist) 
        //     ? (optimalDist - directDist) / optimalDist 
        //     : 0;
        let currentTarget: BattleUnit | undefined = ctx.target;
        let proximity: number = 0;
        if (!isReposition && optimalDist > 1 && currentDirectDist <= optimalDist) {
            // Ми на дистанції І бачимо ціль -> Оцінюємо якість позиції за відстанню
            const ratio = (optimalDist - currentDirectDist) / optimalDist;
            const penalty = ratio * ratio;

            proximity = Math.max(0, 1 - penalty);
        } else {
            proximity = Math.max(0, 1 - (moveCost - stats.minDist) / (stats.maxDist - stats.minDist));
        }

        const secondaryNode = ctx.secondaryTacticalMap?.nodes[hexId];
        if (!isReposition && secondaryNode) {
            let finalSecondaryProximity: number;

            // Якщо ми вже в зоні атаки другорядної цілі:
            // Використовуємо твою "точну" логіку замість (moveCost - minDist)
            const sDirectDist = secondaryNode.directDist ?? 99;
            const sRatio = (optimalDist - sDirectDist) / optimalDist;
            const sPenalty = sRatio * sRatio;
            
            // Отримуємо чисте значення близькості 0...1
            const secondaryProximity = Math.max(0, 1 - sPenalty);

            // Коригуємо вагу другорядної цілі (наприклад, -10% пріоритету)
            finalSecondaryProximity = secondaryProximity * (1 - ctx.weights.DISCIPLINE);

            if (finalSecondaryProximity > proximity) {
                currentTarget = ctx.enemies.find(e => e.unitId === secondaryNode?.targetId);
                proximity = finalSecondaryProximity;
                currentDirectDist = sDirectDist; // Оновлюємо дистанцію для наступних розрахунків (напр. offense)
            }
        }
        
        // --- 2. LETHALITY (Страх померти) ---
        // const effectiveThreat = Math.max(0, rawThreat - protectionValue);
        const effectiveThreat = Math.max(0, rawThreat - protectionValue);
        
        // unitWeight: 0.05 для гобліна, 0.4 для танка.
        // const unitWeight = ctx.unitValue / ctx.totalAlliesValue;

        // ФІКС: Замість Math.pow чи sqrt, використовуємо unitWeight як дільник.
        // Це "роздуває" HP для слабких юнітів, роблячи їх безстрашними.
        // Якщо у гобліна 50 HP, а вага 0.05, його virtualHp для розрахунку = 1000.
        // const virtualHp = unit.maxUnitHp / unitWeight;

        // Використовуємо Math.min(0.9, ...), щоб lethality НІКОЛИ не була 1.0.
        // Це залишає юніту шанс вибрати гекс, навіть якщо там дуже боляче.
        // const lethality = Math.min(0.9, effectiveThreat / virtualHp);

        // 1. Скільки коштує наше 1 HP для нашої армії
        const myValuePerHp = ctx.unitValue / (unit.maxUnitHp);

        // 2. Скільки вартості ми втрачаємо під вогнем на цьому гексі
        const lostValue = effectiveThreat * myValuePerHp;

        // 3. Нормалізуємо (яку частку своєї армії ми підставляємо під удар)
        const lethality = Math.min(1.0, (rawThreat / getUnitTotalHp(ctx.unit)) * (ctx.unitValue / ctx.totalAlliesValue));

        // --- 3. OFFENSE (Агресія) ---
        let offense = 0;
        // if (!isReposition && directDist === optimalDist) {
        //     if (target) {
        //         // 1. Рахуємо, скільки "балів сили" припадає на 1 HP цієї цілі
        //         // Використовуємо MaxHP, щоб ціна за 1 HP була стабільною константою
        //         const targetMaxHp = target.stats.hp * target.count;
        //         const valuePerHp = ctx.targetValue / targetMaxHp;

        //         // 2. Рахуємо, скільки балів вартості ми "відкушуємо" за атаку
        //         const damageValue = (ctx.unitAvgDmg / 4) * valuePerHp;

        //         // 3. Нормалізуємо це відносно загальної сили (щоб отримати 0...1)
        //         // Тепер offense — це частка ворожої армії, яку ми знищуємо цим ударом.
        //         offense = damageValue / ctx.totalEnemiesValue;
                
        //         // Додатково: можна додати невеликий бонус (наприклад, 10-20%) 
        //         // за можливість саме ВБИТИ юніт, але база тепер не залежить від "мало HP".
        //     }
        // }

        // console.log('scoreHex', hexId, ctx.unit.unitId, moveCost, stats, dist, directDist, proximity, lethality, offense);

        return { proximity, lethality, offense };
    }
}